import { useEffect, useRef, useState } from "react";
import { detectCreditBlockGeometryWarnings } from "./blockOverlapDetection";

const setsMatch = (first, second) =>
  first.size === second.size && [...first].every((value) => second.has(value));

const EMPTY_WARNINGS = {
  overlappingBlockIndexes: new Set(),
  gutterOverflowingBlockIndexes: new Set(),
  titleSafeOverflowingBlockIndexes: new Set(),
};

const warningSetsMatch = (first, second) =>
  setsMatch(first.overlappingBlockIndexes, second.overlappingBlockIndexes) &&
  setsMatch(
    first.gutterOverflowingBlockIndexes,
    second.gutterOverflowingBlockIndexes,
  ) &&
  setsMatch(
    first.titleSafeOverflowingBlockIndexes,
    second.titleSafeOverflowingBlockIndexes,
  );

const confirmedWarnings = (current, previous, next) => {
  const confirmSet = (currentSet, previousSet, nextSet) => {
    const confirmed = new Set(
      [...currentSet].filter((index) => nextSet.has(index)),
    );
    nextSet.forEach((index) => {
      if (previousSet.has(index)) confirmed.add(index);
    });
    return confirmed;
  };

  return {
    overlappingBlockIndexes: confirmSet(
      current.overlappingBlockIndexes,
      previous.overlappingBlockIndexes,
      next.overlappingBlockIndexes,
    ),
    gutterOverflowingBlockIndexes: confirmSet(
      current.gutterOverflowingBlockIndexes,
      previous.gutterOverflowingBlockIndexes,
      next.gutterOverflowingBlockIndexes,
    ),
    titleSafeOverflowingBlockIndexes: confirmSet(
      current.titleSafeOverflowingBlockIndexes,
      previous.titleSafeOverflowingBlockIndexes,
      next.titleSafeOverflowingBlockIndexes,
    ),
  };
};

const hasUnconfirmedAddition = (previous, next) =>
  [...next.overlappingBlockIndexes].some(
    (index) => !previous.overlappingBlockIndexes.has(index),
  ) ||
  [...next.gutterOverflowingBlockIndexes].some(
    (index) => !previous.gutterOverflowingBlockIndexes.has(index),
  ) ||
  [...next.titleSafeOverflowingBlockIndexes].some(
    (index) => !previous.titleSafeOverflowingBlockIndexes.has(index),
  );

export function useCreditBlockGeometryWarnings({
  enabled,
  revision,
  targetKey,
}) {
  const [warnings, setWarnings] = useState(() => EMPTY_WARNINGS);
  const scheduleMeasureRef = useRef(null);

  // A project revision changes while the user edits, but the observed preview
  // element does not. Queue a fresh measurement without tearing down and
  // rebuilding hundreds of observers on every keystroke or scrub step.
  useEffect(() => {
    if (enabled) scheduleMeasureRef.current?.();
  }, [enabled, revision]);

  useEffect(() => {
    if (!enabled) {
      setWarnings((current) =>
        current.overlappingBlockIndexes.size ||
        current.gutterOverflowingBlockIndexes.size ||
        current.titleSafeOverflowingBlockIndexes.size
          ? EMPTY_WARNINGS
          : current,
      );
      return undefined;
    }

    setWarnings((current) =>
      current.overlappingBlockIndexes.size ||
      current.gutterOverflowingBlockIndexes.size ||
      current.titleSafeOverflowingBlockIndexes.size
        ? EMPTY_WARNINGS
        : current,
    );

    const previewElement = document.getElementById("preview");
    if (!previewElement) return undefined;

    let cancelled = false;
    let timer = null;
    let previousDetectedWarnings = EMPTY_WARNINGS;

    const measure = () => {
      if (cancelled) return;
      const next = detectCreditBlockGeometryWarnings(previewElement);
      const previous = previousDetectedWarnings;
      const shouldConfirmAgain = hasUnconfirmedAddition(previous, next);

      setWarnings((current) => {
        // Remove resolved warnings immediately, but require a new issue to
        // appear in two measurements before displaying it. This filters out
        // transient geometry while a font or preview scale is settling.
        const confirmed = confirmedWarnings(current, previous, next);
        return warningSetsMatch(current, confirmed) ? current : confirmed;
      });
      previousDetectedWarnings = next;

      if (shouldConfirmAgain) scheduleMeasure(80);
    };

    const scheduleMeasure = (delay = 140) => {
      if (cancelled) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, delay);
    };
    scheduleMeasureRef.current = scheduleMeasure;

    scheduleMeasure(0);
    document.fonts?.ready?.then?.(() => scheduleMeasure(0));

    const onFontsLoaded = () => scheduleMeasure(0);
    document.fonts?.addEventListener?.("loadingdone", onFontsLoaded);

    let mutationObserver;
    if (typeof MutationObserver !== "undefined") {
      mutationObserver = new MutationObserver(() => scheduleMeasure());
      mutationObserver.observe(previewElement, {
        attributes: true,
        attributeFilter: ["class", "style"],
        characterData: true,
        childList: true,
        subtree: true,
      });
    }

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => scheduleMeasure());
      resizeObserver.observe(previewElement);
      previewElement
        .querySelectorAll("[data-credit-block-index]")
        .forEach((element) => resizeObserver.observe(element));
    }

    const onResize = () => scheduleMeasure();
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      document.fonts?.removeEventListener?.("loadingdone", onFontsLoaded);
      window.removeEventListener("resize", onResize);
      if (scheduleMeasureRef.current === scheduleMeasure) {
        scheduleMeasureRef.current = null;
      }
    };
  }, [enabled, targetKey]);

  return warnings;
}

export default function useCreditBlockOverlapWarnings(options) {
  return useCreditBlockGeometryWarnings(options).overlappingBlockIndexes;
}
